/**
 * Live family-report processing state — the combined page's twin of the
 * individual `ProcessingSection` (COMBINED-V2-DATA-MAP §3). Until this
 * existed the combined page rendered BLANK cards while a family run was in
 * flight, with no progress and no polling: the landlord had to guess and
 * reload to discover the report had landed (2026-07-17).
 *
 * Same cadence and mechanics as the individual page so the two can't drift:
 * revalidate the loader every 5s, poll `/resources/family-score-status`
 * every 3s (1s initial delay). Polling uses REF-CALLBACK intervals (React 19
 * cleanup), never useEffect (project rule); the section mounts only while a
 * run is in flight, so unmounting tears the timers down and the revalidated
 * loader flips the page to the real report on its own.
 *
 * The overlay's `variant="family"` (title "Generating Family Report" +
 * FAMILY_TIPS) already existed — it was written for this and never wired up.
 *
 * NOTE: the endpoint's step ticks are TIME-derived (collect → analyze at 3s
 * → score at 10s), not the worker's true stage — a long job parks on the
 * last step. Honest in that it never shows completion before completion, but
 * it is not real progress; same character as the individual page's steps.
 */
import { useCallback, useRef } from 'react'
import { useFetcher, useRevalidator } from 'react-router'
import {
	type ProgressStep,
	ReportProgressOverlay,
} from '#app/components/report-sections/ReportProgressOverlay'
import { ReportSkeleton } from '#app/components/report-sections/ReportSkeleton'

interface FamilyStatusPayload {
	steps?: ProgressStep[]
	overall?: 'pending' | 'processing' | 'completed' | 'failed'
	error?: string
}

/**
 * Invisible marker owning one interval. The ref callback returns a cleanup
 * (React 19), so mounting starts the timer and unmounting clears it — the
 * sanctioned no-useEffect pattern. `onTick` is read through a ref so the
 * stable callback never re-attaches. (Mirrors ProcessingSection's marker;
 * kept local rather than exported to avoid a shared-component change on a
 * bug fix — extract if a third caller ever needs it.)
 */
function IntervalMarker({
	intervalMs,
	initialDelayMs,
	onTick,
}: {
	intervalMs: number
	initialDelayMs?: number
	onTick: () => void
}) {
	const latest = useRef(onTick)
	latest.current = onTick
	const attach = useCallback(
		(node: HTMLElement | null) => {
			if (!node) return
			const tick = () => latest.current()
			const delay =
				initialDelayMs != null ? setTimeout(tick, initialDelayMs) : null
			const interval = setInterval(tick, intervalMs)
			return () => {
				if (delay) clearTimeout(delay)
				clearInterval(interval)
			}
		},
		[intervalMs, initialDelayMs],
	)
	return <span hidden ref={attach} />
}

export function FamilyProcessingSection({ groupId }: { groupId: string }) {
	const revalidator = useRevalidator()
	const statusFetcher = useFetcher<FamilyStatusPayload>()

	const pollStatus = () => {
		try {
			void statusFetcher.load(
				`/resources/family-score-status?groupId=${groupId}`,
			)
		} catch {
			// Route manifest may not be ready on first call; retry next tick.
		}
	}

	const steps = Array.isArray(statusFetcher.data?.steps)
		? statusFetcher.data.steps
		: null
	// The endpoint reports 'pending' before a row exists; the overlay only
	// knows processing|completed|failed, and we are mounted BECAUSE a run is
	// in flight — so anything not terminal reads as processing.
	const raw = statusFetcher.data?.overall
	const overall: 'processing' | 'completed' | 'failed' =
		raw === 'completed' || raw === 'failed' ? raw : 'processing'

	return (
		<>
			<IntervalMarker
				intervalMs={5000}
				onTick={() => void revalidator.revalidate()}
			/>
			<IntervalMarker
				intervalMs={3000}
				initialDelayMs={1000}
				onTick={pollStatus}
			/>
			{steps && steps.length > 0 ? (
				<ReportProgressOverlay
					variant="family"
					steps={steps}
					overall={overall}
				/>
			) : (
				// First tick hasn't answered yet — skeleton, never a blank page.
				<ReportSkeleton />
			)}
		</>
	)
}
